import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import {
  GetWorkspaceIssueSourceSettingsDocument,
  type IssueSourceSettings,
  type SourcePolicy,
  type UpdateIssueSourceSettingsInput,
  UpdateWorkspaceDocument,
} from "#app/lib/data";

// `IssueSourceSettings` is a row alias over the fragment on the schema type of
// the same name.
export type { IssueSourceSettings, SourcePolicy };

// Mirrors the server's own default for a workspace that has never decided:
// a project offers a source. Whether one is required is the Type's call.
const DEFAULT_SETTINGS: IssueSourceSettings = {
  __typename: "IssueSourceSettings",
  projectSourcePolicy: "optional",
  projectDefaultSourceId: null,
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useIssueSourceSettings() {
  const { activeWorkspace } = useWorkspace();
  const workspaceId = activeWorkspace?.workspaceId ?? null;

  const queryVars = useMemo(() => ({ id: workspaceId! }), [workspaceId]);

  const { data, loading, error, refetch } = useQuery(
    GetWorkspaceIssueSourceSettingsDocument,
    {
      variables: queryVars,
      skip: !workspaceId,
      fetchPolicy: "cache-and-network",
    },
  );

  const [updateWorkspaceMutation] = useMutation(UpdateWorkspaceDocument);

  const settings = useMemo<IssueSourceSettings>(
    () => data?.workspace?.issueSourceSettings ?? DEFAULT_SETTINGS,
    [data],
  );

  const updateSetting = useCallback(
    async (patch: UpdateIssueSourceSettingsInput) => {
      if (!workspaceId) return;
      try {
        await updateWorkspaceMutation({
          variables: {
            id: workspaceId,
            input: {
              issueSourceSettings: patch,
            },
          },
        });
      } catch (err) {
        toastMutationError(err, "Failed to update setting");
      }
    },
    [workspaceId, updateWorkspaceMutation],
  );

  return {
    settings,
    hasData: !!data?.workspace,
    isLoading: loading,
    error,
    refetch,
    updateSetting,
  };
}
